import AlertsRequester from '@cobliteam/api-lib/src/dashboard/alerts';
import type { TriggerEvent, TriggerEventNotification } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/events';
import type { EventRule, Rule, RuleMode, TriggerRuleSummary } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/rules';
import type { Trigger, TriggerRecord } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/triggers';
import type { TriggerUserPreferencesRecord } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/users';
import type { TriggerEventsFilters, TriggersFilters } from '@cobliteam/api-lib/src/dashboard/alerts/utils';
import type { PageableContent, UUID } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { endOfDay, startOfDay } from '@cobliteam/utils/date';
import { type UseQueryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useUser from '../useUser';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getAlertsRequester = () => new AlertsRequester({ host: env.alertsApiHost });

const QUERY_KEY = 'ALERTS';

const useCreateTrigger = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: getAlertsRequester().createTriggger,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'TRIGGERS'] });
    },
    meta: {
      successMessage: 'Alerta criado com sucesso!',
      errorMessage: 'Erro ao criar alerta!',
    },
  });
};

const useCreateObservationPeriodTrigger = () =>
  useMutation({
    mutationFn: getAlertsRequester().createObservationPeriodTrigger,
  });

const useEditTrigger = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ triggerId, triggerRecord }: { triggerId: UUID; triggerRecord: TriggerRecord }) => getAlertsRequester().editTrigger(triggerId, triggerRecord),
    onSuccess: (_data, { triggerId }) => {
      Alert.success('Alerta editado com sucesso!');

      // The trigger PUT upserts the bulletin server-side, so evict both caches to force a refetch
      // when the user re-enters the edit page (otherwise the bulletin query hydrates from stale data).
      queryClient.removeQueries({ queryKey: [QUERY_KEY, 'TRIGGERS'] });
      queryClient.removeQueries({ queryKey: [QUERY_KEY, 'TRIGGER', triggerId] });
      queryClient.removeQueries({ queryKey: [QUERY_KEY, 'BULLETIN', triggerId] });
    },
    onError: () => {
      Alert.error('Erro ao editar alerta!');
    },
  });
};

const useDeleteBulletin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ triggerId }: { triggerId: UUID }) => getAlertsRequester().deleteBulletin(triggerId),
    onSuccess: (_data, { triggerId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'BULLETIN', triggerId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'TRIGGER', triggerId] });
    },
    meta: {
      errorMessage: 'Erro ao remover resumo de alertas!',
    },
  });
};

const useGetEventsRules = (mode: RuleMode, options?: Partial<UseQueryOptions<EventRule[]>>) =>
  useQuery({
    queryKey: [QUERY_KEY, 'EVENTS_RULES', mode],
    queryFn: () => getAlertsRequester().getEventsRules(mode),
    ...options,
  });

const useGetRules = (mode?: RuleMode, options?: Partial<UseQueryOptions<Rule[]>>) =>
  useQuery({
    queryKey: [QUERY_KEY, 'RULES', mode],
    queryFn: () => getAlertsRequester().getRules(mode),
    ...options,
  });

const useGetRule = (ruleId: UUID, options?: Partial<UseQueryOptions<Rule>>) =>
  useQuery({
    queryKey: [QUERY_KEY, 'RULES', ruleId],
    queryFn: () => getAlertsRequester().getRule(ruleId),
    enabled: false,
    ...options,
  });

const useInfinityTriggers = (filters: TriggersFilters) => {
  return useInfiniteQuery<PageableContent<Trigger>>({
    initialPageParam: 0,
    queryKey: [QUERY_KEY, 'LIST-TRIGGERS', filters],
    queryFn: ({ pageParam = 0 }) => getAlertsRequester().getTriggers({ ...filters, page: pageParam as number }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.last === false) return pages.length;
      return undefined;
    },
  });
};

const useGetTriggers = (filters?: TriggersFilters, options?: Partial<UseQueryOptions<PageableContent<Trigger>>>) => {
  const session = useUser().getSession();

  return useQuery({
    queryKey: [QUERY_KEY, 'TRIGGERS', filters, session.fleet.id],
    queryFn: () => getAlertsRequester().getTriggers(filters),
    meta: {
      errorMessage: 'Erro ao buscar alerta!',
    },
    ...options,
  });
};

// Drops the triggers list from cache so it re-fetches when the list is shown
// again. Uses removeQueries (not invalidateQueries) — same approach the legacy
// trigger mutations use (see useEditTrigger) — because the app sets
// refetchOnMount: false globally (Dashboard.tsx): an invalidated-but-inactive
// query stays stale on remount, so only eviction forces a fresh fetch.
// Scoped to the list cache: the SDUI edit form reloads its own data via the
// blueprint page query, so only the manage-alerts list needs eviction here.
// Wired into the blueprint BlueprintProvider's onMutationSuccess, since SDUI
// edits hit the blueprint /mutations endpoint and bypass useEditTrigger.
const useRefreshTriggerList = () => {
  const queryClient = useQueryClient();
  return () => queryClient.removeQueries({ queryKey: [QUERY_KEY, 'TRIGGERS'] });
};

const useGetTrigger = (triggerId: UUID, options?: Partial<UseQueryOptions<Trigger>>) => {
  const session = useUser().getSession();

  return useQuery({
    queryKey: [QUERY_KEY, 'TRIGGER', triggerId, session.fleet.id],
    queryFn: () => getAlertsRequester().getTrigger(triggerId),
    meta: {
      errorMessage: 'Erro ao buscar alerta!',
    },
    ...options,
  });
};

const useActiveTrigger = (triggerId: UUID, active: boolean) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => getAlertsRequester().activeTrigger(triggerId, active),
    onSuccess: async () => {
      const label = active ? 'ativado' : 'desativado';
      Alert.success(`Alerta ${label} com sucesso!`);

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'TRIGGERS'],
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'TRIGGER', triggerId],
      });
    },
    onError: () => {
      const label = active ? 'ativar' : 'desativar';
      Alert.error(`Erro ao ${label} alerta!`);
    },
  });
};

const useGetTriggerEvents = (filters: TriggerEventsFilters, options?: Partial<UseQueryOptions<PageableContent<TriggerEvent>>>) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'TRIGGER_EVENTS', filters],
    queryFn: () => {
      try {
        return getAlertsRequester().getTriggerEvents(filters);
      } catch (error) {
        Alert.error('Erro ao buscar eventos!');

        throw error;
      }
    },
    enabled: !!filters.rule,
    ...options,
  });
};

const useGetTriggerUserPreferences = () => {
  const userId = useUser().getUser().id;

  return useQuery({
    queryKey: [QUERY_KEY, 'TRIGGER_USER', userId],
    queryFn: () => {
      try {
        return getAlertsRequester().getTriggerUserPreferences(userId);
      } catch (error) {
        Alert.error('Erro ao buscar usuário!');

        throw error;
      }
    },
  });
};

const useUpdateTriggerUserPreferences = () => {
  const queryClient = useQueryClient();
  const userId = useUser().getUser().id;

  const getActionText = (preferences: TriggerUserPreferencesRecord, previousPreferences?: TriggerUserPreferencesRecord) => {
    if (!previousPreferences) return undefined;

    const changedKey = (Object.keys(preferences) as Array<keyof TriggerUserPreferencesRecord>).find((key) => preferences[key] !== previousPreferences[key]);

    if (!changedKey) return undefined;

    const actionText = `Canal ${preferences[changedKey] ? 'habilitado' : 'desabilitado'} com sucesso`;
    return actionText;
  };

  return useMutation({
    mutationFn: (preferences: TriggerUserPreferencesRecord) => getAlertsRequester().updateTriggerUserPreferences(userId, preferences),
    onMutate: async (preferences: TriggerUserPreferencesRecord) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEY, 'TRIGGER_USER', userId],
      });

      const previousPreferences = queryClient.getQueryData([QUERY_KEY, 'TRIGGER_USER', userId]) as TriggerUserPreferencesRecord | undefined;

      queryClient.setQueryData([QUERY_KEY, 'TRIGGER_USER', userId], preferences);

      // Get the action text describing what was changed
      const actionText = getActionText(preferences, previousPreferences);

      return { previousPreferences, actionText };
    },
    onSuccess: (data, variables, context) => {
      Alert.success(context?.actionText || 'Preferências atualizadas com sucesso!');
    },
    onError: (err, preferences, context) => {
      queryClient.setQueryData([QUERY_KEY, 'TRIGGER_USER', userId], context?.previousPreferences);
      Alert.error('Erro ao atualizar as preferências!');
    },
  });
};

const useGetTriggerEventsByRule = (startTime: string, endTime: string, mode?: RuleMode, vehicleGroupIds?: UUID[], options?: Partial<UseQueryOptions<TriggerRuleSummary[]>>) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'TRIGGER_EVENTS_BY_RULE', startTime, endTime, mode, vehicleGroupIds],
    queryFn: () => getAlertsRequester().getTriggerEventsByRule(startTime, endTime, mode, vehicleGroupIds),
    ...options,
  });
};

const useGetTriggersNotifications = (filters: TriggerEventsFilters) => {
  return useInfiniteQuery<PageableContent<TriggerEventNotification>>({
    queryKey: [QUERY_KEY, 'NOTIFICATIONS', filters],
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) =>
      getAlertsRequester().getTriggerNotifications({
        ...filters,
        page: pageParam as number,
      }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.last === false) return pages.length;
      return undefined;
    },
  });
};

const NOTIFICATIONS_WINDOW_DAYS = 7;
const UNREAD_NOTIFICATIONS_REFETCH_MS = 5 * 60 * 1000;

/** Janela do sino (hoje e os 6 dias anteriores), calculada na hora: aba aberta de um dia para o outro não congela. */
export const getNotificationsWindow = (now = new Date()) => {
  const firstDay = new Date(now);
  firstDay.setDate(now.getDate() - (NOTIFICATIONS_WINDOW_DAYS - 1));
  return { startTime: startOfDay(firstDay).toISOString(), endTime: endOfDay(now).toISOString() };
};

const useGetUnreadNotificationsAmount = () =>
  useQuery({
    // A janela fica fora da key: a queryFn a recalcula a cada busca, e o refetch periódico cobre a virada do dia.
    queryKey: [QUERY_KEY, 'UNREAD_NOTIFICATIONS_AMOUNT'],
    queryFn: () => {
      const range = getNotificationsWindow();
      return getAlertsRequester().getUnreadNotificationsAmount(range.startTime, range.endTime);
    },
    refetchInterval: UNREAD_NOTIFICATIONS_REFETCH_MS,
  });

const useMarkNotificationAsRead = (id: number, createdAt: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => getAlertsRequester().markNotificationAsRead(id, createdAt),
    onMutate: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MARK_NOTIFICATION_AS_READ', id],
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'UNREAD_NOTIFICATIONS_AMOUNT'],
      });
    },
  });
};

const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      const { startTime, endTime } = getNotificationsWindow();
      return getAlertsRequester().markAllNotificationsAsRead(startTime, endTime);
    },
    onMutate: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MARK_ALL_NOTIFICATIONS_AS_READ'],
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'UNREAD_NOTIFICATIONS_AMOUNT'],
      });
    },
  });
};

const useUpdateTriggerEventObservation = (id: string, eventTime: string, observation: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => getAlertsRequester().updateTriggerEventObservation(id, eventTime, observation),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'TRIGGER_EVENTS'],
      });
    },
  });
};

const useDeleteriggerEventObservation = (id: string, eventTime: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => getAlertsRequester().deleteTriggerEventObservation(id, eventTime),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'TRIGGER_EVENTS'],
      });
    },
  });
};

export {
  getAlertsRequester,
  useActiveTrigger,
  useCreateObservationPeriodTrigger,
  useCreateTrigger,
  useDeleteBulletin,
  useDeleteriggerEventObservation,
  useEditTrigger,
  useGetEventsRules,
  useGetRule,
  useGetRules,
  useGetTrigger,
  useGetTriggerEvents,
  useGetTriggerEventsByRule,
  useGetTriggers,
  useGetTriggersNotifications,
  useGetTriggerUserPreferences,
  useGetUnreadNotificationsAmount,
  useInfinityTriggers,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useRefreshTriggerList,
  useUpdateTriggerEventObservation,
  useUpdateTriggerUserPreferences,
};
